import { useRef, type ReactNode } from 'react';
import { CONTAINER } from '../lib/layout.js';
import { gsap, useGSAP, withMotion } from '../lib/motion.js';

export interface PageIntroProps {
  /** Small caps line above the title — where the reader is. */
  eyebrow: string;
  title: string;
  lead: string;
  /** Buttons, typically WhatsApp and a way into the fleet. */
  actions?: ReactNode;
}

/**
 * The top of a standalone page: one line of orientation, the headline, a
 * sentence and the two things worth doing from here.
 *
 * Deliberately not `PageHeader` from the kit — that one is sized for the
 * admin, where a page title sits above a table. These pages are read, so
 * the headline is display-sized and the measure is capped.
 *
 * The four parts rise in on arrival, the same quick settle the hero uses,
 * so a page reached from the header does not simply appear fully formed.
 * `from` tweens again: nothing is hidden until GSAP decides to run.
 */
export function PageIntro({ eyebrow, title, lead, actions }: PageIntroProps) {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () =>
      withMotion(() => {
        gsap.from('[data-intro]', { y: 18, autoAlpha: 0, duration: 0.55, stagger: 0.08 });
      }),
    { scope: root },
  );

  return (
    <section className="border-b border-border-default bg-surface">
      <div ref={root} className={`${CONTAINER} py-16 md:py-20`}>
        <p data-intro className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
          {eyebrow}
        </p>
        <h1
          data-intro
          className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tighter md:text-6xl"
        >
          {title}
        </h1>
        {lead ? (
          <p data-intro className="mt-5 max-w-xl text-lg leading-relaxed text-text-muted">
            {lead}
          </p>
        ) : null}
        {actions ? (
          <div data-intro className="mt-8 flex flex-wrap items-center gap-3">
            {actions}
          </div>
        ) : null}
      </div>
    </section>
  );
}
